import React, { useCallback, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import ModalLayoutWithTitle from "../Layouts/ModalLayoutWithTitle";
import Button from "../Button/ButtonVariant";
import moment from "moment";
import DatePicker from "./DatePicker";
import { DateTime } from "luxon";

interface ITogglePinModal {
  postId: string;
  isOpen: boolean;
  onClose: () => void;
  isPinned: boolean;
  onPinHandler: (pinTill: string) => void;
}

const TogglePinModal = ({
  isOpen,
  onClose,
  isPinned,
  onPinHandler,
}: ITogglePinModal) => {
  const { t } = useTranslation();
  const [isPinTillClicked, setPinTillClicked] = useState(false);
  const [pinTill, setPinTill] = useState<DateTime>(null);

  const onClickPinTill = useCallback(() => {
    setPinTillClicked(!isPinTillClicked);
  }, [isPinTillClicked]);

  const onDateChange = useCallback((date: Date | null): void => {
    if (!date) {
      return;
    }
    setPinTill(DateTime.fromJSDate(date));
  }, []);

  const onPin = useCallback(() => {
    const unformattedDate = pinTill ? pinTill.toISODate() : null;
    onPinHandler(unformattedDate);
    onClose();
  }, [pinTill, onPinHandler]);

  const onCancelHandler = () => {
    if (isPinTillClicked) {
      setPinTill(null);
      setPinTillClicked(false);
      return;
    }

    return onClose();
  };

  const PinModal = useCallback(() => {
    return (
      <div className={styles.pin_container}>
        {!isPinTillClicked && (
          <Button
            buttonType="secondary-outline"
            title={
              pinTill
                ? t("Pin until {{date}}", {
                    date: pinTill?.toLocaleString({
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    }),
                  })
                : t("Pin until...")
            }
            onClickFunc={onClickPinTill}
          />
        )}

        {isPinTillClicked && (
          <div className={styles.pin_till_container}>
            <DatePicker
              placeholderText={t("Select Date")}
              setDate={(d) => onDateChange(d.toJSDate())}
              date={pinTill}
              maxDate={null}
              minDate={moment().add(1, "days").toDate()}
            />
            <Button
              buttonType="secondary-outline"
              title={t("Confirm Date Selection")}
              onClickFunc={onClickPinTill}
              disabled={!pinTill}
            />
          </div>
        )}
      </div>
    );
  }, [t, isPinTillClicked, pinTill]);

  return (
    <ModalLayoutWithTitle
      isOpen={isOpen}
      onClose={onClose}
      headerTitle={!isPinned ? t("Pin this post?") : t("Unpin from top?")}
      className={styles.modal_layout}
    >
      <div className={styles.container}>
        <div className={styles.header_description}>
          {!isPinned
            ? t(
                "This post will be pinned to the top for everyone in this Space.",
              )
            : t(
                "This post will be unpinned from top for everyone in this Space.",
              )}
        </div>
        <div className={styles.content_container}>
          <Button
            buttonType="primary"
            title={!isPinned ? t("Pin") : t("Unpin")}
            onClickFunc={onPin}
          />
          {!isPinned && <PinModal />}
          <Button
            buttonType="secondary-outline"
            title={t("Cancel")}
            onClickFunc={onCancelHandler}
          />
        </div>
      </div>
    </ModalLayoutWithTitle>
  );
};

export default TogglePinModal;
